import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { adminUsersAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

// Role styles
const roleStyles = {
  system_admin: { color: '#7c3aed', label: 'System Admin' },
  administrator: { color: '#dc2626', label: 'Administrator' },
  clinical_designer: { color: '#0f766e', label: 'Clinical Designer' },
  researcher: { color: '#0284c7', label: 'Researcher' },
}

function RoleIndicator({ role }) {
  const style = roleStyles[role] || { color: '#71717a', label: role }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function ActiveStatusIndicator({ isActive }) {
  const style = isActive ? { color: '#16a34a', label: 'Active' } : { color: '#71717a', label: 'Inactive' }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

// Permission level styles
const permissionStyles = {
  full: { color: '#16a34a' },
  'full + base': { color: '#0f766e' },
  read: { color: '#0284c7' },
  limited: { color: '#ca8a04' },
  none: { color: '#71717a' },
}

function PermissionIndicator({ level }) {
  const style = permissionStyles[level] || { color: '#71717a' }
  return (
    <Typography variant="body2" sx={{ color: style.color, fontSize: '0.75rem' }}>
      {level}
    </Typography>
  )
}

const ROLES = [
  {
    value: 'system_admin',
    label: 'System Admin',
    description: 'Sanexus platform admin - manages base content and all system features',
    permissions: {
      content: 'full + base',
      rules: 'full + base',
      patients: 'full',
      analytics: 'full',
      system: 'full',
    },
  },
  {
    value: 'administrator',
    label: 'Administrator',
    description: 'Full access to all organization features',
    permissions: {
      content: 'full',
      rules: 'full',
      patients: 'full',
      analytics: 'full',
      system: 'full',
    },
  },
  {
    value: 'clinical_designer',
    label: 'Clinical Designer',
    description: 'Full access to intervention content and composition rules',
    permissions: {
      content: 'full',
      rules: 'full',
      patients: 'full',
      analytics: 'full',
      system: 'none',
    },
  },
  {
    value: 'researcher',
    label: 'Researcher',
    description: 'Read-only access to content and rules, full analytics access',
    permissions: {
      content: 'read',
      rules: 'read',
      patients: 'limited',
      analytics: 'full',
      system: 'none',
    },
  },
]

function UserManagement() {
  const { adminUser: currentUser, hasPermission } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'clinical_designer',
    is_active: true,
  })
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const canCreate = hasPermission('admin_users', 'create')
  const canUpdate = hasPermission('admin_users', 'update')
  const canDelete = hasPermission('admin_users', 'delete')

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminUsersAPI.getAll()
      setUsers(data)
    } catch (err) {
      console.error('Error fetching users:', err)
      setError(err.response?.data?.error || 'Failed to load admin users')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user)
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        is_active: user.is_active,
      })
    } else {
      setEditingUser(null)
      setFormData({
        email: '',
        name: '',
        role: 'clinical_designer',
        is_active: true,
      })
    }
    setFormErrors({})
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingUser(null)
    setFormErrors({})
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.email) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format'
    }
    if (!formData.name) {
      errors.name = 'Name is required'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      setSaving(true)
      if (editingUser) {
        await adminUsersAPI.update(editingUser.id, {
          name: formData.name,
          role: formData.role,
          is_active: formData.is_active,
        })
      } else {
        await adminUsersAPI.create(formData)
      }
      handleCloseDialog()
      fetchUsers()
    } catch (err) {
      console.error('Error saving user:', err)
      setFormErrors({
        submit: err.response?.data?.error || 'Failed to save user'
      })
    } finally {
      setSaving(false)
    }
  }

  const handleOpenDeleteDialog = (user) => {
    setEditingUser(user)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setEditingUser(null)
  }

  const handleDelete = async () => {
    if (!editingUser) return

    try {
      setSaving(true)
      await adminUsersAPI.delete(editingUser.id)
      handleCloseDeleteDialog()
      fetchUsers()
    } catch (err) {
      console.error('Error deleting user:', err)
      setError(err.response?.data?.error || 'Failed to delete user')
      handleCloseDeleteDialog()
    } finally {
      setSaving(false)
    }
  }

  const getRoleInfo = (roleValue) => {
    return ROLES.find((r) => r.value === roleValue) || ROLES[0]
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">User Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage admin portal users and their role-based permissions.
          </Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add User
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <Paper sx={{ mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const isCurrentUser = user.id === currentUser?.id
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" component="span">
                          {user.name}
                        </Typography>
                        {isCurrentUser && (
                          <Typography variant="caption" color="text.secondary">
                            (you)
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <RoleIndicator role={user.role} />
                    </TableCell>
                    <TableCell>
                      <ActiveStatusIndicator isActive={user.is_active} />
                    </TableCell>
                    <TableCell>
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      {canUpdate && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && !isCurrentUser && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteDialog(user)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No admin users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Role Permissions Matrix */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
          Role Permissions Matrix
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell align="center">Content</TableCell>
                <TableCell align="center">Rules</TableCell>
                <TableCell align="center">Patients</TableCell>
                <TableCell align="center">Analytics</TableCell>
                <TableCell align="center">System</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ROLES.map((role) => (
                <TableRow key={role.value}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {role.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {role.description}
                    </Typography>
                  </TableCell>
                  {Object.entries(role.permissions).map(([key, value]) => (
                    <TableCell key={key} align="center">
                      <PermissionIndicator level={value} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formErrors.submit}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            error={!!formErrors.name}
            helperText={formErrors.name}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
            disabled={!!editingUser}
            error={!!formErrors.email}
            helperText={formErrors.email || (editingUser ? 'Email cannot be changed' : '')}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              label="Role"
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  <Box>
                    <Typography variant="body2">{role.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {role.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {editingUser && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            {editingUser
              ? 'Changes will take effect on the user\'s next login.'
              : 'The user must have a matching AWS Cognito account to log in.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : editingUser ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{editingUser?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UserManagement
